import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { CssBaseline } from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: '#1B4965' },
    secondary: { main: '#2E7D32' },
    background: {
      default: '#F6F8FC',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#0F172A',
      secondary: '#475569',
    },
  },
  shape: { borderRadius: 12 },
  typography: {
    fontFamily: [
      'Segoe UI Variable',
      'system-ui',
      '-apple-system',
      'Segoe UI',
      'Roboto',
      'Helvetica',
      'Arial',
      'sans-serif',
    ].join(','),
    h4: { fontWeight: 700, letterSpacing: -0.2 },
    h5: { fontWeight: 700, letterSpacing: -0.15 },
    h6: { fontWeight: 650, letterSpacing: -0.1 },
    button: { fontWeight: 600 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: { scrollBehavior: 'smooth' },
        body: { minHeight: '100vh' },
        '#root': { minHeight: '100vh' },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: ({ theme }) => ({
          border: `1px solid ${theme.palette.divider}`,
          backgroundImage: 'none',
        }),
      },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0 },
    },
    MuiToolbar: {
      styleOverrides: {
        root: { minHeight: 64 },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: Math.max(10, theme.shape.borderRadius),
          textTransform: 'none',
          transition: theme.transitions.create(['transform', 'background-color', 'box-shadow'], {
            duration: theme.transitions.duration.shorter,
          }),
        }),
        contained: ({ theme }) => ({
          boxShadow: '0 10px 24px rgba(15, 23, 42, 0.12)',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 14px 30px rgba(15, 23, 42, 0.16)',
          },
          '&:active': { transform: 'translateY(0px)' },
          [theme.breakpoints.down('sm')]: {
            '&:hover': { transform: 'none' },
          },
        }),
        outlined: ({ theme }) => ({
          borderColor: theme.palette.divider,
          '&:hover': { borderColor: theme.palette.primary.main },
        }),
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: Math.max(10, theme.shape.borderRadius),
          backgroundColor: theme.palette.background.paper,
        }),
      },
    },
    MuiTextField: {
      defaultProps: { size: 'small' },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: Math.max(16, theme.shape.borderRadius),
          border: `1px solid ${theme.palette.divider}`,
          overflow: 'hidden',
          backgroundColor: theme.palette.background.paper,
        }),
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          borderCollapse: 'separate',
          borderSpacing: 0,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: ({ theme }) => ({
          fontWeight: 700,
          fontSize: 12,
          letterSpacing: 0.2,
          textTransform: 'uppercase',
          color: theme.palette.text.secondary,
          backgroundColor: theme.palette.action.hover,
          borderBottom: `1px solid ${theme.palette.divider}`,
          paddingTop: 12,
          paddingBottom: 12,
        }),
        body: ({ theme }) => ({
          borderBottom: `1px solid ${theme.palette.divider}`,
          paddingTop: 12,
          paddingBottom: 12,
          overflowWrap: 'anywhere',
          wordBreak: 'break-word',
          whiteSpace: 'normal',
        }),
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&:hover td': {
            backgroundColor: theme.palette.action.hover,
          },
          '&:last-child td': {
            borderBottom: 0,
          },
        }),
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: Math.max(12, theme.shape.borderRadius),
          overflow: 'hidden',
          '&:before': { display: 'none' },
          '&.Mui-expanded': { margin: 0 },
          '& + &': { marginTop: theme.spacing(1.25) },
        }),
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: ({ theme }) => ({
          minHeight: 56,
          '&.Mui-expanded': { minHeight: 56 },
          '& .MuiAccordionSummary-content.Mui-expanded': { margin: theme.spacing(1.5, 0) },
        }),
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: Math.max(12, theme.shape.borderRadius),
          border: `1px solid ${theme.palette.divider}`,
        }),
      },
    },
    MuiChip: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 999,
          border: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
        }),
      },
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <App />
    </ThemeProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
